import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listApplications,
  listJobs,
  listCandidates,
  updateApplicationStatus,
  type ApplicationDto,
  type JobDto,
  type CandidateDto,
} from '../../lib/recruitment';

export const Route = createFileRoute('/rekrut/pipeline')({
  component: PipelinePage,
});

function PipelinePage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <PipelineContent />
    </ProtectedRoute>
  );
}

const STAGES: ApplicationDto['status'][] = ['new', 'screening', 'interview', 'offer', 'hired', 'rejected'];

const STAGE_META: Record<ApplicationDto['status'], { label: string; color: string; headerBg: string }> = {
  new:        { label: 'New',        color: 'border-gray-200 bg-gray-50',   headerBg: 'bg-gray-100 text-gray-700' },
  screening:  { label: 'Screening',  color: 'border-blue-200 bg-blue-50',   headerBg: 'bg-blue-100 text-blue-800' },
  interview:  { label: 'Interview',  color: 'border-indigo-200 bg-indigo-50', headerBg: 'bg-indigo-100 text-indigo-800' },
  offer:      { label: 'Offer',      color: 'border-purple-200 bg-purple-50', headerBg: 'bg-purple-100 text-purple-800' },
  hired:      { label: 'Hired',      color: 'border-green-200 bg-green-50',  headerBg: 'bg-green-100 text-green-800' },
  rejected:   { label: 'Rejected',   color: 'border-red-200 bg-red-50',     headerBg: 'bg-red-100 text-red-700' },
};

function PipelineContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:rekrut');

  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [jobs, setJobs] = useState<JobDto[]>([]);
  const [candidates, setCandidates] = useState<CandidateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      listApplications(companyId),
      listJobs(companyId),
      listCandidates(companyId),
    ]).then(([appRes, jobRes, cRes]) => {
      setLoading(false);
      if (appRes.success && appRes.data) setApplications(appRes.data.applications);
      if (jobRes.success && jobRes.data) setJobs(jobRes.data.jobs);
      if (cRes.success && cRes.data) setCandidates(cRes.data.candidates);
    });
  }, [companyId]);

  const getJob = (id: string) => jobs.find((j) => j.id === id);
  const getCandidate = (id: string) => candidates.find((c) => c.id === id);

  async function moveApplication(appId: string, newStatus: ApplicationDto['status']) {
    setMovingId(appId);
    const res = await updateApplicationStatus(appId, newStatus, companyId);
    setMovingId(null);
    if (res.success && res.data) {
      setApplications((prev) =>
        prev.map((a) => (a.id === appId ? res.data!.application : a))
      );
    }
  }

  const byStage = (stage: ApplicationDto['status']) =>
    applications.filter((a) => a.status === stage);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to={'/rekrut' as any} className="text-sm text-blue-600 hover:underline">← Back to Rekrut</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Hiring Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">{applications.length} total applications</p>
        </div>
      </div>

      {loading ? (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {STAGES.map((s) => (
            <div key={s} className="w-52 shrink-0 space-y-2">
              <div className="h-8 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-24 bg-gray-100 rounded-xl animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {STAGES.map((stage) => {
              const meta = STAGE_META[stage];
              const cards = byStage(stage);
              return (
                <div
                  key={stage}
                  className={`w-52 shrink-0 rounded-xl border-2 ${meta.color} flex flex-col`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragging && canManage) moveApplication(dragging, stage);
                    setDragging(null);
                  }}
                >
                  {/* Column header */}
                  <div className={`px-3 py-2 rounded-t-xl ${meta.headerBg}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold">{meta.label}</span>
                      <span className="text-xs opacity-70">{cards.length}</span>
                    </div>
                  </div>

                  {/* Cards */}
                  <div className="flex-1 p-2 space-y-2 min-h-24">
                    {cards.map((app) => {
                      const job = getJob(app.jobId);
                      const candidate = getCandidate(app.candidateId);
                      const isMoving = movingId === app.id;
                      return (
                        <div
                          key={app.id}
                          draggable={canManage}
                          onDragStart={() => setDragging(app.id)}
                          onDragEnd={() => setDragging(null)}
                          onClick={() => {
                            if (!dragging) navigate({ to: '/rekrut/candidates/$candidateId' as any, params: { candidateId: app.candidateId } as any });
                          }}
                          className={`bg-white rounded-lg border p-2.5 shadow-sm text-xs cursor-pointer hover:shadow-md transition-shadow ${isMoving ? 'opacity-50' : ''} ${dragging === app.id ? 'ring-2 ring-blue-400 cursor-grab' : ''}`}
                        >
                          <p className="font-semibold text-gray-800 truncate">{candidate?.name ?? '—'}</p>
                          <p className="text-gray-400 truncate">{job?.title ?? '—'}</p>
                          <div className="mt-1.5 flex items-center justify-between">
                            <span className="text-blue-600 font-medium">{Math.round(app.matchScore * 100)}%</span>
                            {canManage && (
                              <select
                                className="text-xs border rounded px-1 py-0.5 text-gray-600 bg-white"
                                value={app.status}
                                onChange={(e) => moveApplication(app.id, e.target.value as ApplicationDto['status'])}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {STAGES.map((s) => (
                                  <option key={s} value={s}>{STAGE_META[s].label}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {cards.length === 0 && (
                      <p className="text-xs text-center opacity-40 pt-4 pb-2">empty</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {canManage && (
        <p className="text-xs text-gray-400 mt-2">Drag cards between columns or use the dropdown to move applications.</p>
      )}
    </div>
  );
}
